import SkeletonBox from '@components/V4/SkeletonBox';
import {
  GetParentViewEnrollmentTimelineDTO,
  ParentTimelineEnrollment,
  ParentTimelineSession,
} from '@shared/clientTypes/DTOs/Parents.DTO';
import { GetParentViewUniversitiesApplyingToDTO } from '@shared/clientTypes/DTOs/ParentsDTO';
import { useTranslation } from 'apps/agora/src/TranslationContext';
import styles from 'apps/agora/src/styles/components/parent-timeline.module.scss';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useEffect, useMemo, useRef, useState } from 'react';

const PIXELS_PER_MONTH = 80;
const LABEL_COLUMN_PX = 240;
const DEADLINE_LABEL_MIN_GAP_PX = 110;

const MONTHS_RO = [
  'Ian',
  'Feb',
  'Mar',
  'Apr',
  'Mai',
  'Iun',
  'Iul',
  'Aug',
  'Sep',
  'Oct',
  'Noi',
  'Dec',
];
const MONTHS_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthCell {
  y: number;
  m: number;
  isYearStart: boolean;
  isQuarter: boolean;
}

const buildMonthCells = (start: Date, end: Date): MonthCell[] => {
  const cells: MonthCell[] = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  const endY = end.getFullYear();
  const endM = end.getMonth();
  while (y < endY || (y === endY && m <= endM)) {
    cells.push({ y, m, isYearStart: m === 0, isQuarter: m % 3 === 0 });
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return cells;
};

const pctForDate = (d: Date, rangeStart: Date, totalMs: number) =>
  Math.max(0, Math.min(100, ((d.getTime() - rangeStart.getTime()) / totalMs) * 100));

const fmtDate = (d: Date, lang: 'en' | 'ro') => {
  const months = lang === 'ro' ? MONTHS_RO : MONTHS_EN;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtDateShort = (d: Date, lang: 'en' | 'ro') => {
  const months = lang === 'ro' ? MONTHS_RO : MONTHS_EN;
  return `${d.getDate()} ${months[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
};

interface EnrollmentTimelineProps {
  timelineData?: GetParentViewEnrollmentTimelineDTO;
  universitiesData?: GetParentViewUniversitiesApplyingToDTO;
  isLoading: boolean;
}

const EnrollmentTimeline = ({
  timelineData,
  universitiesData,
  isLoading,
}: EnrollmentTimelineProps) => {
  const { t, language } = useTranslation();
  const [expandedEnrollmentId, setExpandedEnrollmentId] = useState<string | null>(null);

  const today = useMemo(() => new Date(), []);
  const wrapRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);

  const { rangeStart, rangeEnd, monthCells, enrollments } = useMemo(() => {
    const enrollmentList: ParentTimelineEnrollment[] = timelineData?.enrollments ?? [];

    const universityDeadlines = (universitiesData?.goals ?? [])
      .flatMap((g) => [
        g.deadline ? new Date(g.deadline).getTime() : null,
        g.resultsDate ? new Date(g.resultsDate).getTime() : null,
      ])
      .filter((t): t is number => t !== null);

    const enrollmentDates = enrollmentList
      .flatMap((e) => [e.startDate, e.endDate])
      .filter(Boolean)
      .map((d) => new Date(d as string).getTime());

    const allDates = [...enrollmentDates, ...universityDeadlines, today.getTime()];
    if (allDates.length === 0) {
      return { rangeStart: null, rangeEnd: null, monthCells: [], enrollments: enrollmentList };
    }

    const minTs = Math.min(...allDates);
    const maxTs = Math.max(...allDates);

    const halfMs = Math.max(today.getTime() - minTs, maxTs - today.getTime());
    const rangeStartTs = today.getTime() - halfMs;
    const rangeEndTs = today.getTime() + halfMs;

    const startRef = new Date(rangeStartTs);
    const endRef = new Date(rangeEndTs);
    const start = new Date(startRef.getFullYear(), startRef.getMonth(), 1);
    const end = new Date(endRef.getFullYear(), endRef.getMonth() + 1, 0);

    return {
      rangeStart: start,
      rangeEnd: end,
      monthCells: buildMonthCells(start, end),
      enrollments: enrollmentList,
    };
  }, [timelineData, universitiesData, today]);

  useEffect(() => {
    if (!wrapRef.current || !todayRef.current) return;
    const wrap = wrapRef.current;
    const todayEl = todayRef.current;
    const todayRect = todayEl.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const todayCenterInWrap =
      todayRect.left - wrapRect.left + wrap.scrollLeft + todayRect.width / 2;
    wrap.scrollLeft = todayCenterInWrap - wrap.clientWidth / 2;
  }, [rangeStart, rangeEnd, monthCells.length]);

  if (isLoading) {
    return (
      <section className="flex flex-col gap-3">
        <h2 className="font-raleway font-bold">{t('parent_timeline_title')}</h2>
        <SkeletonBox className="w-full h-[400px]" />
      </section>
    );
  }

  if (!enrollments.length || !rangeStart || !rangeEnd) {
    return (
      <section className="flex flex-col gap-3 p-6 rounded-xl bg-surfaceObject">
        <h2 className="font-raleway font-bold">{t('parent_timeline_title')}</h2>
        <p className="text-customGrey text-sm">{t('parent_timeline_empty')}</p>
      </section>
    );
  }

  const totalMs = rangeEnd.getTime() - rangeStart.getTime();
  const monthsLabel = language === 'ro' ? MONTHS_RO : MONTHS_EN;
  const todayInRange = today >= rangeStart && today <= rangeEnd;
  const todayPct = todayInRange ? pctForDate(today, rangeStart, totalMs) : 0;
  const laneWidthPx = monthCells.length * PIXELS_PER_MONTH;
  const timelineWidthPx = LABEL_COLUMN_PX + laneWidthPx;

  const deadlineEntries = (universitiesData?.goals ?? [])
    .filter((g) => {
      if (!g.deadline) return false;
      const dd = new Date(g.deadline);
      return dd >= rangeStart && dd <= rangeEnd;
    })
    .map((g) => {
      const dd = new Date(g.deadline);
      const leftPct = pctForDate(dd, rangeStart, totalMs);
      return {
        _id: g._id,
        leftPct,
        leftPx: (leftPct / 100) * laneWidthPx,
        label: g.name?.split(' ').slice(0, 2).join(' ') ?? '',
      };
    })
    .sort((a, b) => a.leftPx - b.leftPx);

  const lastPxInLevel: number[] = [];
  const deadlinesWithLevels = deadlineEntries.map((entry) => {
    let level = lastPxInLevel.findIndex((lp) => entry.leftPx - lp >= DEADLINE_LABEL_MIN_GAP_PX);
    if (level === -1) level = lastPxInLevel.length;
    lastPxInLevel[level] = entry.leftPx;
    return { ...entry, level };
  });

  const resultsEntries = (universitiesData?.goals ?? [])
    .filter((g) => {
      if (!g.resultsDate) return false;
      const dd = new Date(g.resultsDate);
      return dd >= rangeStart && dd <= rangeEnd;
    })
    .map((g) => {
      const dd = new Date(g.resultsDate as Date);
      const leftPct = pctForDate(dd, rangeStart, totalMs);
      return {
        _id: g._id,
        leftPct,
        leftPx: (leftPct / 100) * laneWidthPx,
        label: g.name?.split(' ').slice(0, 2).join(' ') ?? '',
      };
    })
    .sort((a, b) => a.leftPx - b.leftPx);

  const lastPxInResultsLevel: number[] = [];
  const resultsWithLevels = resultsEntries.map((entry) => {
    let level = lastPxInResultsLevel.findIndex(
      (lp) => entry.leftPx - lp >= DEADLINE_LABEL_MIN_GAP_PX
    );
    if (level === -1) level = lastPxInResultsLevel.length;
    lastPxInResultsLevel[level] = entry.leftPx;
    return { ...entry, level };
  });

  const dotStatusClass = (status: ParentTimelineSession['status']) => {
    switch (status) {
      case 'done':
        return styles.dotDone;
      case 'scheduled':
        return styles.dotScheduled;
      default:
        return styles.dotUnscheduled;
    }
  };

  const statusLabel = (status: ParentTimelineSession['status']) => {
    switch (status) {
      case 'done':
        return t('parent_timeline_status_done');
      case 'scheduled':
        return t('parent_timeline_status_scheduled');
      default:
        return t('parent_timeline_status_unscheduled');
    }
  };

  const toggleExpand = (enrollmentId: string) => {
    setExpandedEnrollmentId((prev) => (prev === enrollmentId ? null : enrollmentId));
  };

  return (
    <section className="flex flex-col gap-3 p-6 rounded-xl bg-surfaceObject">
      <div>
        <h2 className="font-raleway font-bold">{t('parent_timeline_title')}</h2>
        <p className="text-customGrey text-sm mt-1">{t('parent_timeline_subtitle')}</p>
      </div>

      <div className={styles.wrap} ref={wrapRef}>
        <div className={styles.timeline} style={{ width: `${timelineWidthPx}px` }}>
          <div className={styles.header}>
            <div className={styles.headerSpacer}>{t('parent_timeline_column_header')}</div>
            <div
              className={styles.months}
              style={{ gridTemplateColumns: `repeat(${monthCells.length}, 1fr)` }}
            >
              {monthCells.map((cell, idx) => (
                <div
                  key={`${cell.y}-${cell.m}-${idx}`}
                  className={mergeClassNames(
                    styles.month,
                    cell.isYearStart ? styles.monthYear : cell.isQuarter ? styles.monthQuarter : ''
                  )}
                >
                  {cell.isYearStart ? `${monthsLabel[cell.m]} ${cell.y}` : monthsLabel[cell.m]}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.body}>
            {enrollments.map((enrollment) => {
              const courseStartIso = enrollment.startDate ?? enrollment.endDate;
              const courseEndIso = enrollment.endDate ?? enrollment.startDate;
              if (!courseStartIso || !courseEndIso) return null;

              const courseStart = new Date(courseStartIso);
              const courseEnd = new Date(courseEndIso);
              const startPct = pctForDate(courseStart, rangeStart, totalMs);
              const endPct = pctForDate(courseEnd, rangeStart, totalMs);

              const done = enrollment.sessions.filter((s) => s.status === 'done').length;
              const total = enrollment.totalSessions || enrollment.sessions.length;
              const progPct = total ? Math.round((done / total) * 100) : 0;

              const doneDatedSessions = enrollment.sessions.filter(
                (s) => s.status === 'done' && s.date
              );
              const lastDoneDate = doneDatedSessions.length
                ? new Date(doneDatedSessions[doneDatedSessions.length - 1].date as string)
                : null;
              const lastDonePct = lastDoneDate
                ? pctForDate(lastDoneDate, rangeStart, totalMs)
                : null;

              const isExpanded = expandedEnrollmentId === enrollment._id;

              return (
                <div key={enrollment._id}>
                  <div className={styles.row}>
                    <button
                      type="button"
                      className={mergeClassNames(
                        styles.rowLabel,
                        isExpanded ? styles.rowLabelExpanded : '',
                        'text-left'
                      )}
                      onClick={() => toggleExpand(enrollment._id)}
                    >
                      <div className={styles.courseName}>
                        <span className={styles.expandIcon}>▶</span>
                        <span style={{ color: enrollment.color }}>●</span>
                        <span>{enrollment.title}</span>
                      </div>
                      <div className={styles.courseMeta}>
                        <span
                          className={styles.mentorDot}
                          style={{ background: enrollment.color }}
                        />
                        <span>{enrollment.mentor.name}</span>
                        <span className={styles.progressCount}>
                          {done}/{total}
                        </span>
                      </div>
                      <div className={styles.courseDates}>
                        {fmtDateShort(courseStart, language as 'en' | 'ro')} —{' '}
                        {fmtDateShort(courseEnd, language as 'en' | 'ro')}
                      </div>
                      <div className={styles.miniBar}>
                        <div
                          className={styles.miniFill}
                          style={{ width: `${progPct}%`, background: enrollment.color }}
                        />
                      </div>
                    </button>

                    <div className={styles.lane}>
                      <div
                        className={styles.laneBg}
                        style={{
                          left: `${startPct.toFixed(2)}%`,
                          width: `${Math.max(0, endPct - startPct).toFixed(2)}%`,
                        }}
                      />
                      {lastDonePct !== null && (
                        <div
                          className={styles.laneDone}
                          style={{
                            left: `${startPct.toFixed(2)}%`,
                            width: `${Math.max(0, lastDonePct - startPct).toFixed(2)}%`,
                            background: enrollment.color,
                          }}
                        />
                      )}

                      <div
                        className={mergeClassNames(
                          styles.courseBoundary,
                          styles.courseBoundaryStart
                        )}
                        style={{ left: `${startPct.toFixed(2)}%`, background: enrollment.color }}
                      >
                        <span className={styles.courseBoundaryLabel}>
                          {fmtDateShort(courseStart, language as 'en' | 'ro')}
                        </span>
                      </div>
                      <div
                        className={mergeClassNames(styles.courseBoundary, styles.courseBoundaryEnd)}
                        style={{ left: `${endPct.toFixed(2)}%`, background: enrollment.color }}
                      >
                        <span className={styles.courseBoundaryLabel}>
                          {fmtDateShort(courseEnd, language as 'en' | 'ro')}
                        </span>
                      </div>

                      {enrollment.sessions.map((session) => {
                        if (!session.date || session.status === 'unscheduled') return null;
                        const sd = new Date(session.date);
                        const leftPct = pctForDate(sd, rangeStart, totalMs);
                        const isToday = sd.toDateString() === today.toDateString();
                        return (
                          <button
                            type="button"
                            key={session._id}
                            className={mergeClassNames(
                              styles.dot,
                              dotStatusClass(session.status),
                              isToday ? styles.dotToday : ''
                            )}
                            style={{ left: `${leftPct.toFixed(2)}%` }}
                          >
                            <span className={styles.tooltip}>
                              <strong>{enrollment.title}</strong>
                              <br />
                              {t('parent_timeline_session')} {session.num} •{' '}
                              {statusLabel(session.status)}
                              <br />
                              {fmtDate(sd, language as 'en' | 'ro')}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}

            {deadlinesWithLevels.map((entry) => (
              <div
                key={entry._id}
                className={styles.deadlineLine}
                style={{
                  left: `calc(${LABEL_COLUMN_PX}px + (100% - ${LABEL_COLUMN_PX}px) * ${(
                    entry.leftPct / 100
                  ).toFixed(4)})`,
                }}
              >
                <div
                  className={styles.deadlineLabel}
                  style={{ top: `${-10 - entry.level * 22}px` }}
                >
                  {entry.label}
                </div>
              </div>
            ))}

            {resultsWithLevels.map((entry) => (
              <div
                key={`results-${entry._id}`}
                className={styles.resultsLine}
                style={{
                  left: `calc(${LABEL_COLUMN_PX}px + (100% - ${LABEL_COLUMN_PX}px) * ${(
                    entry.leftPct / 100
                  ).toFixed(4)})`,
                }}
              >
                <div className={styles.resultsLabel} style={{ top: `${-10 - entry.level * 22}px` }}>
                  {entry.label}
                </div>
              </div>
            ))}

            {todayInRange && (
              <div
                ref={todayRef}
                className={styles.todayLine}
                style={{
                  left: `calc(${LABEL_COLUMN_PX}px + (100% - ${LABEL_COLUMN_PX}px) * ${(
                    todayPct / 100
                  ).toFixed(4)})`,
                }}
              >
                <div className={styles.todayLabel}>{t('parent_timeline_today')}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {(() => {
        const expanded = enrollments.find((e) => e._id === expandedEnrollmentId);
        if (!expanded) return null;
        const startIso = expanded.startDate ?? expanded.endDate;
        const endIso = expanded.endDate ?? expanded.startDate;
        if (!startIso || !endIso) return null;
        const startD = new Date(startIso);
        const endD = new Date(endIso);
        return (
          <div className={styles.descPanel} style={{ borderLeftColor: expanded.color }}>
            <p>
              <strong>{expanded.title}</strong> — {expanded.mentor.name} • {expanded.totalSessions}{' '}
              {t('parent_sessions').toLowerCase()}
            </p>
            {expanded.description && (
              <p style={{ marginTop: '0.625rem', whiteSpace: 'pre-line' }} className="text-white">
                {expanded.description}
              </p>
            )}
            <p style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.75rem' }}>
              {fmtDate(startD, language as 'en' | 'ro')} — {fmtDate(endD, language as 'en' | 'ro')}
            </p>
          </div>
        );
      })()}

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={mergeClassNames(styles.legendSwatch, styles.dotDone)} />
          {t('parent_timeline_legend_done')}
        </div>
        <div className={styles.legendItem}>
          <div className={mergeClassNames(styles.legendSwatch, styles.dotScheduled)} />
          {t('parent_timeline_legend_scheduled')}
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendLine} />
          {t('parent_timeline_legend_deadline')}
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendLineResults} />
          {t('parent_timeline_legend_results')}
        </div>
      </div>
    </section>
  );
};

export default EnrollmentTimeline;
